/**
 * 配送員 marker 的移動平滑（純數學，不依賴任何地圖套件）。
 *
 * 來源是後台 FleetMap 的 CourierMap.js —— 那些門檻值是實跑車隊調出來的，不要憑感覺重寫。
 * 抽成無 UI 的純函式有兩個目的：一是客戶端用 Leaflet、後台用 maplibre，共用不了元件只能共用數學；
 * 二是這段有明確的輸入輸出，可以寫測試，塞在 render 裡就不行。
 *
 * 做法是「路徑跟隨」而不是「每收到一筆就插值 N 秒」：
 * 把還沒走到的回報點排成一條待走路徑，每一幀的速度由「還剩多少路 ÷ 還剩多少時間」決定，
 * 時間預算來自「預估的下一包抵達時間」。這樣資料快就走快、資料慢就走慢，會自己收斂。
 *
 * 固定時長的插值在這裡行不通：推送間隔是 1～2 秒浮動的，時長一旦寫死，
 * 推送慢一點車就提早走完、停著等下一包，再衝刺追上——「停頓 → 衝刺」每個週期來一次，
 * 那就是肉眼看到的卡頓。
 *
 * 客戶端刻意「沒有」搬過來的東西：
 * - RoadSnapper（道路貼合）：要下載整份縣市道路 geojson，LINE webview 裡不划算。要做請做在後端。
 * - accuracy circle / indoor mode：那是車隊在判斷「這個點可不可信」，客戶不需要這層資訊。
 * - 歷史軌跡尾巴：等於揭露配送員剛從哪裡來（＝上一位客戶家），隱私上不能給。
 * - 轉彎偵測的中繼點插補：後端每 10 公尺就回報一次，點夠密，不需要再自己補點。
 */

export const SMOOTHING_CONFIG = {
  /**
   * 跳點判定的速度上限（公尺／秒，約 150 km/h）。
   *
   * 這裡刻意不用「距離門檻」。後台 FleetMap 用的是絕對距離 100 公尺，那是給
   * 1~2 秒一次的 WebSocket 推送用的——2 秒跑 100 公尺等於時速 180，明顯是壞資料。
   * 客戶端現在也是同一種 AppSync 即時推送，正常間隔同樣是 1~2 秒；但斷線重連、
   * 分頁切回前台之後，兩筆之間的間隔可能拉長到好幾秒甚至幾十秒，這時 100 公尺
   * 只是正常行駛。改成看「兩筆之間的隱含速度」，不管間隔長短都對。
   */
  TELEPORT_SPEED_MPS: 42,
  /** 位移小於這個公尺數一律不算跳點，不管間隔多短（GPS 原地抖動不該觸發瞬移） */
  TELEPORT_MIN_DISTANCE_M: 100,
  /** 拿不到取樣間隔時的保守距離門檻：這個距離不管隔多久都不會是連續行駛 */
  TELEPORT_FALLBACK_M: 1000,
  /**
   * 時間差超過這個值就直接到位，不做動畫。
   *
   * 速度門檻擋的是壞資料，這條擋的是「資料沒問題，但中間斷了一段」——
   * 分頁切到背景五分鐘，配送員開了 3 公里，換算 10 m/s 是完全正常的行車速度，
   * 不會被速度門檻攔下來，但要是照著走，車會用幾秒鐘飛完那 3 公里。
   */
  MAX_ANIMATE_GAP_MS: 8_000,
  /**
   * 位移小於這個公尺數的回報不排進待走路徑。
   * GPS 在原地會抖個一兩公尺，排進去只會讓車在停等紅燈時左右蠕動。
   */
  MIN_BEARING_UPDATE_DISTANCE_M: 3,

  /**
   * 這批待走路徑要在「預估的下一包抵達時間 × 這個倍率」內走完。
   * 大於 1 表示永遠留一點路當緩衝，不會每個週期都走到沒路可走然後停住。
   */
  BUDGET_FACTOR: 1.35,
  /** 預估間隔的上下限，擋掉開頭幾筆或網路抖動算出來的離譜值 */
  MIN_INTERVAL_MS: 500,
  MAX_INTERVAL_MS: 3_000,
  /** 速度／角度平滑的時間常數。用 dt 正規化，所以換幀率手感不變 */
  SPEED_TAU_MS: 350,
  BEARING_TAU_MS: 170,
  /** 安全上限（公尺／秒）。純粹防爛資料，正常情況碰不到 */
  MAX_SPEED_MPS: 50,
  /** 低於這個速度且幾乎沒路可走就視為停住 */
  STOP_SPEED_MPS: 0.2,
  /** 這一幀移動不到這個距離就不要轉方向，免得停住時原地抖 */
  MIN_MOVE_FOR_BEARING_M: 0.05,
  /**
   * 前視轉向：車頭看向「路徑上前方這麼多秒」的位置，而不是死盯著下一個點。
   * 後端點大約一秒一個，死盯下一點會變成「直線衝到轉角 → 原地轉一下 → 再直線衝」，
   * 看起來就是轉彎很突兀。看遠一點，車頭會在進彎前就開始轉、出彎後才轉完。
   */
  LOOK_AHEAD_SEC: 0.55,
  LOOK_AHEAD_MIN_M: 4,
  /** 待走路徑最多留幾個點；超過代表畫面嚴重落後，寧可丟掉舊的 */
  MAX_PENDING_POINTS: 20,
  /** 單幀最多計入這麼多毫秒：分頁切回前台時 dt 會是好幾秒，不夾住會一次衝出去 */
  MAX_FRAME_MS: 100,
} as const;

export interface LatLngPoint {
  lat: number;
  lng: number;
}

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;
const EARTH_R = 6371000;

/**
 * 兩點間距（公尺）。
 *
 * 這裡自帶一份 haversine 而不是引 addressTool 的同名函式：那支檔案會連帶把
 * 地址 API 與 axios 拉進來，讓這個「純數學、零相依」的模組變成拖著一條相依鏈的東西
 * （測試跑不動就是第一個徵兆）。九行公式的重複，換模組邊界乾淨，划算。
 */
export const distanceBetween = (a: LatLngPoint, b: LatLngPoint): number => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return EARTH_R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/** a → b 的方位角（度，0 = 正北，順時針）。 */
export const bearingBetween = (a: LatLngPoint, b: LatLngPoint): number => {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

/** 從一點朝某個方位角走指定公尺數後的位置。 */
export const moveInDirection = (from: LatLngPoint, bearing: number, meters: number): LatLngPoint => {
  const d = meters / EARTH_R;
  const br = toRad(bearing);
  const lat1 = toRad(from.lat);
  const lng1 = toRad(from.lng);
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br));
  const lng2 =
    lng1 +
    Math.atan2(Math.sin(br) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return { lat: toDeg(lat2), lng: toDeg(lng2) };
};

/**
 * 角度的低通濾波。
 * 必須走「最短弧」，否則 359° → 1° 會被算成往回轉 358°，marker 會原地打一整圈。
 */
export const smoothBearing = (current: number, target: number, factor: number): number => {
  const delta = ((target - current + 540) % 360) - 180;
  return (current + delta * factor + 360) % 360;
};

/**
 * 這兩筆座標之間是不是「跳點」（GPS 漂移、斷線重連後的新位置）。
 * @param sampleGapMs 兩筆座標的時間差；沒有就退回保守的距離門檻
 */
export const isTeleport = (meters: number, sampleGapMs?: number | null): boolean => {
  if (sampleGapMs != null && sampleGapMs > SMOOTHING_CONFIG.MAX_ANIMATE_GAP_MS) return true;
  if (meters < SMOOTHING_CONFIG.TELEPORT_MIN_DISTANCE_M) return false;
  if (sampleGapMs == null || sampleGapMs <= 0) return meters > SMOOTHING_CONFIG.TELEPORT_FALLBACK_M;
  return meters / (sampleGapMs / 1000) > SMOOTHING_CONFIG.TELEPORT_SPEED_MPS;
};

/** 畫面上這一刻要畫的位置與車頭方向。 */
export interface MarkerRenderState {
  position: LatLngPoint;
  bearing: number;
}

/**
 * 跟隨狀態。pending 是「還沒走到的回報點」，position 永遠落在這條路徑的起點上。
 */
export interface FollowState extends MarkerRenderState {
  /** 目前的算繪速度（公尺／秒），不是後端回報的速度 */
  speedMps: number;
  pending: LatLngPoint[];
  /** 推送間隔的指數移動平均；還沒有兩筆資料時是 null */
  intervalMs: number | null;
  /** 上一筆座標的回報時間（epoch ms） */
  lastTs: number | null;
  /** 最後一次「真的在移動」時的方向，停住時維持它，不要歸零 */
  lastMovementBearing: number;
}

export const createFollowState = (position: LatLngPoint, bearing = 0): FollowState => ({
  position,
  bearing,
  speedMps: 0,
  pending: [],
  intervalMs: null,
  lastTs: null,
  lastMovementBearing: bearing,
});

/**
 * 收到一筆新座標。
 *
 * 正常情況是把它接到待走路徑的尾端——不改變目前畫在哪，只是多給一段路可以走。
 * 跳點或中間斷了太久就清空路徑直接到位：那種情況下照著走只會變成用幾秒飛完幾公里。
 */
export const pushSample = (
  state: FollowState,
  position: LatLngPoint,
  ts: number | null | undefined,
  bearing?: number | null,
): FollowState => {
  const gapMs = ts != null && state.lastTs != null ? ts - state.lastTs : null;
  const tail = state.pending.length ? state.pending[state.pending.length - 1] : state.position;
  const dist = distanceBetween(tail, position);

  if (isTeleport(dist, gapMs)) {
    const snapped = bearing ?? state.lastMovementBearing;
    return {
      ...state,
      position,
      bearing: snapped,
      lastMovementBearing: snapped,
      speedMps: 0,
      pending: [],
      lastTs: ts ?? state.lastTs,
    };
  }

  // 預估封包間隔，advanceFollow 用它換算這批路徑該花多久走完
  const intervalMs =
    gapMs != null && gapMs > 0
      ? state.intervalMs != null
        ? state.intervalMs * 0.7 + gapMs * 0.3
        : gapMs
      : state.intervalMs;

  // 原地抖動不值得排進路徑：排了只會讓車在停等時左右蠕動
  const pending =
    dist < SMOOTHING_CONFIG.MIN_BEARING_UPDATE_DISTANCE_M
      ? state.pending
      : [...state.pending, position];

  return {
    ...state,
    pending:
      pending.length > SMOOTHING_CONFIG.MAX_PENDING_POINTS
        ? pending.slice(-SMOOTHING_CONFIG.MAX_PENDING_POINTS)
        : pending,
    intervalMs,
    lastTs: ts ?? state.lastTs,
  };
};

/** 從目前位置沿著待走路徑算到最後一點的總長度（公尺）。 */
export const pendingPathLength = (state: FollowState): number => {
  let total = 0;
  let from = state.position;
  for (const point of state.pending) {
    total += distanceBetween(from, point);
    from = point;
  }
  return total;
};

/**
 * 沿著待走路徑往前量 aheadM 公尺，回傳「從目前位置看向那個點」的方位角。
 * 車頭因此會提前開始轉，而不是衝到轉角才原地扭一下。
 */
export const lookAheadBearing = (state: FollowState, aheadM: number): number | null => {
  let budget = aheadM;
  let from = state.position;
  let target: LatLngPoint | null = null;

  for (const to of state.pending) {
    const d = distanceBetween(from, to);
    if (d < 0.01) continue;
    if (d >= budget) {
      target = moveInDirection(from, bearingBetween(from, to), budget);
      break;
    }
    budget -= d;
    from = to;
    target = to;
  }

  if (!target || distanceBetween(state.position, target) < 0.1) return null;
  return bearingBetween(state.position, target);
};

/**
 * 推進一幀。
 *
 * 速度 = 還剩多少路 ÷ 還剩多少時間。路多就快一點、路少就慢一點，會自己收斂；
 * 資料遲到時是平順減速到停，而不是沿著舊方向滑行出去。
 */
export const advanceFollow = (state: FollowState, dtMs: number): FollowState => {
  const dtSec = Math.min(Math.max(dtMs, 0), SMOOTHING_CONFIG.MAX_FRAME_MS) / 1000;
  if (dtSec === 0) return state;

  const C = SMOOTHING_CONFIG;
  const remaining = pendingPathLength(state);
  const intervalMs = Math.min(
    Math.max(state.intervalMs ?? C.MIN_INTERVAL_MS, C.MIN_INTERVAL_MS),
    C.MAX_INTERVAL_MS,
  );
  const budgetSec = (intervalMs * C.BUDGET_FACTOR) / 1000;

  const targetSpeed = Math.min(remaining / budgetSec, C.MAX_SPEED_MPS);
  const kSpeed = 1 - Math.exp(-dtSec / (C.SPEED_TAU_MS / 1000));
  let speedMps = state.speedMps + (targetSpeed - state.speedMps) * kSpeed;

  if (speedMps < C.STOP_SPEED_MPS && remaining < 0.5) {
    speedMps = 0;
    /**
     * 剩不到半公尺就直接落在終點。
     * 速度歸零卻把那個點留在路徑裡的話，之後每一幀的預算都是 0、永遠吃不掉它，
     * 路徑不空、迴圈就停不下來——畫面什麼都沒動，rAF 卻一直在轉。
     */
    if (state.pending.length) {
      const last = state.pending[state.pending.length - 1];
      return { ...state, position: last, pending: [], speedMps: 0, bearing: state.lastMovementBearing };
    }
  }

  if (!state.pending.length) {
    return { ...state, speedMps, bearing: state.lastMovementBearing };
  }

  /**
   * 這一幀的距離預算要完整走掉：跨過一個路徑點之後還有預算就繼續走。
   * 寫成 min(步長, 到下一點的距離) 的話，跨點那一幀剩下的預算會被丟掉，
   * 每吃一個路徑點就少走一截——那正是肉眼看到的細微頓點。
   */
  let budget = speedMps * dtSec;
  let position = state.position;
  const pending = [...state.pending];

  while (budget > 0 && pending.length) {
    const next = pending[0];
    const dist = distanceBetween(position, next);
    if (dist < 0.1) {
      position = next;
      pending.shift();
      continue;
    }
    if (dist <= budget) {
      position = next;
      budget -= dist;
      pending.shift();
    } else {
      position = moveInDirection(position, bearingBetween(position, next), budget);
      budget = 0;
    }
  }

  const moved = speedMps * dtSec;
  const next: FollowState = { ...state, position, pending, speedMps };

  if (moved < C.MIN_MOVE_FOR_BEARING_M) {
    return { ...next, bearing: state.lastMovementBearing };
  }

  const aheadM = Math.max(C.LOOK_AHEAD_MIN_M, speedMps * C.LOOK_AHEAD_SEC);
  const aim = lookAheadBearing(next, aheadM);
  if (aim === null) return { ...next, bearing: state.lastMovementBearing };

  const kBearing = 1 - Math.exp(-dtSec / (C.BEARING_TAU_MS / 1000));
  const bearing = smoothBearing(state.bearing, aim, kBearing);
  return { ...next, bearing, lastMovementBearing: bearing };
};

/**
 * 還有事情要做嗎？沒有的話呼叫端可以把動畫迴圈停掉。
 *
 * 只看路徑有沒有走完：路走完了就沒有任何東西可以動，再轉下去只是空耗。
 * 速度刻意不歸零——下一筆推送進來時從既有的速度接著平滑，比從零重新起步自然。
 */
export const isFollowIdle = (state: FollowState): boolean => state.pending.length === 0;
